import React, { useState } from "react";
import {
  Modal,
  Radio,
  Collapse,
  Checkbox,
  Row,
  Col,
  Button,
  Typography,
} from "antd";
import { useSelector } from "react-redux";
import {
  getAllDepartmentList,
  getLocations,
  getRoleList,
} from "redux/selectors";

const { Panel } = Collapse;
const { Text } = Typography;

const SendAnnouncementModal = ({
  open,
  onClose,
  onConfirm,
  selectedRoles = [],
  selectedDepartments = [],
  selectedLocations = [],
  selectedEmploymentTypes = [],
  setSelectedRoles,
  setSelectedDepartments,
  setSelectedLocations,
  setSelectedEmploymentTypes,
}) => {
  const [sendType, setSendType] = useState("specific");
  const [activePanel, setActivePanel] = useState("role");

  const roleList = useSelector(getRoleList) || [];
  const departmentList = useSelector(getAllDepartmentList) || [];
  const locationList = useSelector(getLocations) || [];

  const employmentTypes = [
    "Casual",
    "Contract",
    "Full time",
    "Paid Internship",
    "Part time",
    "Training",
    "Unpaid Internship",
    "Probation",
  ];

  const handleRoleSelectAll = (checked) => {
    setSelectedRoles(checked ? roleList.map((r) => r.roleId) : []);
  };

  const handleDepartmentSelectAll = (checked) => {
    setSelectedDepartments(
      checked ? departmentList.map((d) => d.department_id) : []
    );
  };

  const handleLocationSelectAll = (checked) => {
    setSelectedLocations(checked ? locationList.map((l) => l.location_id) : []);
  };

  const handleEmploymentTypeSelectAll = (checked) => {
    setSelectedEmploymentTypes(checked ? employmentTypes : []);
  };

  const handleConfirm = () => {
    const payload = {
      sendType,
      roleIds: sendType === "all" ? [] : selectedRoles,
      departmentIds: sendType === "all" ? [] : selectedDepartments,
      locationIds: sendType === "all" ? [] : selectedLocations,
      employeementtype: sendType === "all" ? [] : selectedEmploymentTypes,
    };

    console.log("Announcement Payload:", payload);
    onConfirm?.(payload);
    onClose();
  };

  const totalSelected =
    selectedRoles.length +
    selectedDepartments.length +
    selectedLocations.length +
    selectedEmploymentTypes.length;

  return (
    <Modal
      open={open}
      title="Send announcement to"
      width={650}
      footer={null}
      centered
      onCancel={onClose}
    >
      {/* Radio */}
      <Radio.Group
        value={sendType}
        onChange={(e) => setSendType(e.target.value)}
        style={{ marginBottom: 20 }}
      >
        <Radio value="all">All Employees</Radio>
        <br />
        <Radio value="specific">Specific Employees</Radio>
      </Radio.Group>

      {sendType === "specific" && (
        <>
          <Row justify="space-between" style={{ marginBottom: 10 }}>
            <Text type="secondary">Specify the category</Text>
            <Text strong>{totalSelected} Selected</Text>
          </Row>

          <Collapse
            accordion
            activeKey={activePanel}
            onChange={(key) => setActivePanel(key)}
          >
            {/* ROLE */}
            <Panel header={`Role (${selectedRoles.length})`} key="role">
              <Row justify="space-between">
                <Checkbox
                  checked={selectedRoles.length === roleList.length}
                  indeterminate={
                    selectedRoles.length > 0 &&
                    selectedRoles.length < roleList.length
                  }
                  onChange={(e) => handleRoleSelectAll(e.target.checked)}
                >
                  All
                </Checkbox>
                <Button type="link" onClick={() => setSelectedRoles([])}>
                  Reset
                </Button>
              </Row>

              <Checkbox.Group
                value={selectedRoles}
                onChange={setSelectedRoles}
                style={{ width: "100%", marginTop: 12 }}
              >
                <Row gutter={[16, 12]}>
                  {roleList.map((r) => (
                    <Col span={8} key={r.roleId}>
                      <Checkbox value={r.roleId}>{r.roleName}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Panel>

            {/* DEPARTMENT */}
            <Panel
              header={`Department (${selectedDepartments.length})`}
              key="department"
            >
              <Row justify="space-between">
                <Checkbox
                  checked={selectedDepartments.length === departmentList.length}
                  indeterminate={
                    selectedDepartments.length > 0 &&
                    selectedDepartments.length < departmentList.length
                  }
                  onChange={(e) => handleDepartmentSelectAll(e.target.checked)}
                >
                  All
                </Checkbox>
                <Button type="link" onClick={() => setSelectedDepartments([])}>
                  Reset
                </Button>
              </Row>

              <Checkbox.Group
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                style={{ width: "100%", marginTop: 12 }}
              >
                <Row gutter={[16, 12]}>
                  {departmentList.map((d) => (
                    <Col span={8} key={d.department_id}>
                      <Checkbox value={d.department_id}>
                        {d.department_name}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Panel>

            {/* LOCATION */}
            <Panel
              header={`Location (${selectedLocations.length})`}
              key="location"
            >
              <Row justify="space-between">
                <Checkbox
                  checked={selectedLocations.length === locationList.length}
                  indeterminate={
                    selectedLocations.length > 0 &&
                    selectedLocations.length < locationList.length
                  }
                  onChange={(e) => handleLocationSelectAll(e.target.checked)}
                >
                  All
                </Checkbox>
                <Button type="link" onClick={() => setSelectedLocations([])}>
                  Reset
                </Button>
              </Row>

              <Checkbox.Group
                value={selectedLocations}
                onChange={setSelectedLocations}
                style={{ width: "100%", marginTop: 12 }}
              >
                <Row gutter={[16, 12]}>
                  {locationList.map((l) => (
                    <Col span={8} key={l.location_id}>
                      <Checkbox value={l.location_id}>
                        {l.location_name}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Panel>

            {/* EMPLOYMENT TYPE */}
            <Panel
              header={`Employment Type (${selectedEmploymentTypes.length})`}
              key="employmentType"
            >
              <Row justify="space-between">
                <Checkbox
                  checked={
                    selectedEmploymentTypes.length === employmentTypes.length
                  }
                  indeterminate={
                    selectedEmploymentTypes.length > 0 &&
                    selectedEmploymentTypes.length < employmentTypes.length
                  }
                  onChange={(e) =>
                    handleEmploymentTypeSelectAll(e.target.checked)
                  }
                >
                  All
                </Checkbox>
                <Button
                  type="link"
                  onClick={() => setSelectedEmploymentTypes([])}
                >
                  Reset
                </Button>
              </Row>

              <Checkbox.Group
                value={selectedEmploymentTypes}
                onChange={setSelectedEmploymentTypes}
                style={{ width: "100%", marginTop: 12 }}
              >
                <Row gutter={[16, 12]}>
                  {employmentTypes.map((type) => (
                    <Col span={8} key={type}>
                      <Checkbox value={type}>{type}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Panel>
          </Collapse>
        </>
      )}

      {/* Footer */}
      <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
        <Col>
          <Button onClick={onClose}>Cancel</Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default SendAnnouncementModal;
